import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Heading,
    Alert,
    AlertIcon,
    AlertDescription,
    useDisclosure,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { Button } from 'components/v4';
import { breadCrumbStyling } from 'components/forms/new/formStyles';
import { toast } from 'react-toastify';

import { validationSchema } from './consts';
import { updateSite, deleteSite } from './api';
import TextField from './formFields/TextField';
import SelectField from './formFields/SelectField';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import walkthroughIds from './walkthroughIds';

const Edit = ({
    site,
    onBack,
    onSuccess,
    requiredScope,
    addresses = [],
    pbxOptions = [],
}) => {
    const { darkMode } = useSelector((s) => s.settings);
    const [error, setError] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const addressOptions = addresses.map((a) => ({
        value: a.id,
        label: a.description || a.companyName || a.id,
    }));

    const initialValues = {
        name: site?.name || '',
        description: site?.description || '',
        // address field holds the addressID UUID (normalised by api.js as `address`)
        address: site?.addressID || site?.address || '',
        pbx: site?.pbx || '',
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        setError(null);
        try {
            await updateSite(site.id, values, requiredScope);
            onSuccess();
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                'An error occurred. Please try again.';
            setError(message);
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await deleteSite(site.id, requiredScope);
            onClose();
            onSuccess();
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                    err?.message ||
                    'Failed to delete site.',
            );
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Box>
            <Breadcrumb sx={breadCrumbStyling} mb={4}>
                <BreadcrumbItem>
                    <BreadcrumbLink as="button" onClick={onBack}>
                        Sites
                    </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem isCurrentPage>
                    <BreadcrumbLink>Edit Site</BreadcrumbLink>
                </BreadcrumbItem>
            </Breadcrumb>

            <Heading size="lg" mb={2}>
                Edit Site
            </Heading>
            <Box color="gray.500" mb={6} fontSize="sm">
                Update the 8×8 site configuration.
            </Box>

            {error && (
                <Alert status="error" mb={4}>
                    <AlertIcon />
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
                validateOnChange={false}
                validateOnBlur>
                {({ isSubmitting }) => (
                    <Form>
                        <Box
                            bg={darkMode ? 'dark.tcap' : 'white'}
                            borderRadius={15}
                            p={8}
                            maxW={895}
                            boxShadow="sm">
                            <TextField
                                name="name"
                                label="Site Name"
                                description="A friendly name to identify this site."
                                required
                                darkMode={darkMode}
                                walkthroughId={walkthroughIds.ufSiteName}
                                placeholder="e.g. Melbourne HQ"
                            />
                            <TextField
                                name="description"
                                label="Description"
                                description="Optional description for this site."
                                darkMode={darkMode}
                                placeholder="e.g. Main office location"
                            />
                            <SelectField
                                name="address"
                                label="Address"
                                description="The physical address the site is associated with."
                                options={addressOptions}
                                darkMode={darkMode}
                                walkthroughId={walkthroughIds.ufAddress}
                                placeholder="Please select"
                            />
                            <SelectField
                                name="pbx"
                                label="PBX"
                                description="The 8×8 PBX this site connects to."
                                options={pbxOptions}
                                darkMode={darkMode}
                                walkthroughId={walkthroughIds.ufPbx}
                                placeholder="Please select"
                            />
                        </Box>

                        <Box
                            display="flex"
                            gap={3}
                            mt={6}
                            maxW={895}
                            justifyContent="space-between">
                            <Box display="flex" gap={3}>
                                <Button
                                    type="submit"
                                    isLoading={isSubmitting}
                                    loadingText="Saving"
                                    data-walkthroughid={
                                        walkthroughIds.ufEditBtn
                                    }>
                                    Save
                                </Button>
                                <Button
                                    variant="outline"
                                    bg="white"
                                    onClick={onBack}
                                    isDisabled={isSubmitting}
                                    data-walkthroughid={
                                        walkthroughIds.ufBackBtn
                                    }>
                                    Cancel
                                </Button>
                            </Box>
                            <Button
                                colorScheme="red"
                                variant="solid"
                                onClick={onOpen}
                                isDisabled={isSubmitting}
                                data-walkthroughid={walkthroughIds.ufDeleteBtn}>
                                Delete Site
                            </Button>
                        </Box>
                    </Form>
                )}
            </Formik>

            <DeleteConfirmationModal
                isOpen={isOpen}
                onClose={onClose}
                onConfirm={handleDelete}
                siteName={site?.name}
                isDeleting={isDeleting}
            />
        </Box>
    );
};

export default Edit;
